import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { recordTransferApply, recordTransferPreview } from '@/shared/api/generated/content/content';
import type { TransferItemBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Dialog, DialogClose, DialogContent } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import styles from './PromoteDialog.module.scss';

const ACTIONS: Record<
  TransferItemBody['action'],
  { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' }
> = {
  create: { label: 'появится', tone: 'success' },
  update: { label: 'обновится', tone: 'warning' },
  link: { label: 'уже там', tone: 'neutral' },
  trash: { label: 'в корзину', tone: 'danger' },
};

export interface PromoteDialogProps {
  siteID: string;
  /** Записи Development; пусто — окно закрыто. */
  recordIDs: string[];
  onClose: () => void;
  onDone?: () => void;
}

/**
 * Перенос записей Development на Production.
 *
 * До переноса видно всё, что переедет: выбранные записи, записи, на которые
 * они ссылаются (автор статьи, общий баннер), и сколько файлов скопируется.
 * На проде копия — черновик: на сайт её выпускают там, проверив, — или сразу,
 * галочкой «Опубликовать» (опубликованным на деве значением). Записи из
 * корзины дева уводят в корзину свои копии на проде. Порядок записей едет
 * вместе с ними.
 */
export function PromoteDialog({ siteID, recordIDs, onClose, onDone }: PromoteDialogProps) {
  const open = recordIDs.length > 0;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [publish, setPublish] = useState(false);

  const preview = useQuery({
    queryKey: ['record-transfer', siteID, recordIDs, publish],
    enabled: open,
    queryFn: async () => {
      const response = await recordTransferPreview(siteID, { record_ids: recordIDs, publish });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на предпросмотр переноса');
      }

      return response.data;
    },
  });

  const items = preview.data?.items ?? [];
  const blocked = preview.data?.blocked ?? [];
  const media = preview.data?.media ?? 0;
  const copying = items.some((item) => item.action !== 'trash');

  const apply = async () => {
    setBusy(true);

    try {
      const response = await recordTransferApply(siteID, { record_ids: recordIDs, publish });

      if (response.status !== 201) {
        throw new Error('Неожиданный ответ на перенос');
      }

      const pairs = response.data.records ?? [];
      const single = pairs.length === 1 ? pairs[0] : undefined;

      const done = response.data;
      const summary = [
        done.created > 0 && `заведено: ${done.created}`,
        done.updated > 0 && `обновлено: ${done.updated}`,
        done.published > 0 && `опубликовано: ${done.published}`,
        done.trashed > 0 && `в корзину: ${done.trashed}`,
      ].filter(Boolean);

      toast.success('Перенесено на Production', {
        description:
          (summary.length > 0 ? `${summary.join(', ')}. ` : 'Изменений не было. ') +
          (publish ? '' : 'Новое на проде — черновиком: опубликуйте его там, когда проверите.'),
        ...(single === undefined
          ? {}
          : {
              action: {
                label: 'Открыть',
                onClick: () => {
                  void navigate({
                    to: '/sites/$siteId/$env/r/$recordId',
                    params: { siteId: siteID, env: 'prod', recordId: single.target_id },
                  });
                },
              },
            }),
      });

      void queryClient.invalidateQueries({ queryKey: ['records'] });
      void queryClient.invalidateQueries({ queryKey: ['record-transfer', siteID] });
      onDone?.();
      onClose();
    } catch (cause) {
      toast.error('Не удалось перенести', {
        description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          onClose();
        }
      }}
    >
      {open && (
        <DialogContent
          title="Перенести на Production"
          description="Записи появятся на проде черновиками, уже перенесённые обновятся вместе с местом в порядке. Удалённые на деве уйдут в корзину и на проде."
          size="md"
        >
          <div className={styles['body']}>
            {preview.isPending ? (
              <p className={styles['muted']}>Смотрим, что переедет…</p>
            ) : preview.isError ? (
              <p className={styles['muted']}>
                {preview.error instanceof ApiError
                  ? preview.error.detail
                  : 'Не удалось посчитать перенос.'}
              </p>
            ) : (
              <>
                {blocked.length > 0 && (
                  <div className={styles['blocked']} role="alert">
                    <TriangleAlert size={16} />
                    <ul>
                      {blocked.map((item) => (
                        <li key={item.type_name}>{item.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <ul className={styles['items']}>
                  {items.map((item) => {
                    const action = ACTIONS[item.action];

                    return (
                      <li key={item.id} className={styles['item']}>
                        <span className={styles['item__title']}>
                          {item.title === '' ? 'Без названия' : item.title}
                        </span>
                        <span className={styles['muted']}>
                          {item.type_name}
                          {item.dependency && ' · на неё ссылаются'}
                        </span>
                        <Badge size="sm" tone={action.tone}>
                          {action.label}
                        </Badge>
                        {item.publish && (
                          <Badge size="sm" tone="accent">
                            опубликуется
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {items.length === 0 && (
                  <p className={styles['muted']}>
                    Переносить нечего: у удалённых записей нет копий на Production.
                  </p>
                )}

                {copying && (
                  <Checkbox
                    checked={publish}
                    onCheckedChange={(value) => {
                      setPublish(value === true);
                    }}
                  >
                    Сразу опубликовать на Production то, что опубликовано на Development
                  </Checkbox>
                )}

                {media > 0 && (
                  <p className={styles['muted']}>
                    Файлов скопируется: {media}. Сами файлы не дублируются — копия ссылается на тот
                    же файл.
                  </p>
                )}
              </>
            )}
          </div>

          <footer className={styles['footer']}>
            <DialogClose asChild>
              <Button variant="ghost" tone="neutral">
                Отмена
              </Button>
            </DialogClose>
            <Button
              iconRight={<ArrowRight size={15} />}
              loading={busy}
              disabled={preview.data === undefined || blocked.length > 0 || items.length === 0}
              onClick={() => {
                void apply();
              }}
            >
              Перенести
            </Button>
          </footer>
        </DialogContent>
      )}
    </Dialog>
  );
}
